/**
 * The second step of sign-in, at /login/mfa.
 *
 * LoginPage has navigated here since it was written, and the route was never
 * registered — so the catch-all sent every MFA user straight back to /login.
 * That is the defect this page and its route close.
 *
 * Completing the challenge returns the same payload as a password-only
 * sign-in ({ accessToken, tenants, claims }), so this finishes the same way
 * LoginPage does: select the first workspace, or go and create one. Without
 * the workspace step the session landed on /create-workspace even for a user
 * who already had one.
 *
 * One field accepts either a six-digit authenticator code or a recovery code.
 * Asking the user which kind they hold would be asking them to explain their
 * own emergency; the API tries both.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Card, Input, PageHeader } from "@/components/ui";
import { post } from "@/lib/api/client";
import { whileSigningIn } from "@/lib/api/sign-in";
import { useAuthStore, type AtlasClaims } from "@/lib/auth/store";
import { errorMessage, useTranslation } from "@/lib/i18n";
import { LanguageSwitch } from "./LanguageSwitch";

/** What the screen quotes: an authenticator code, a recovery code, its life. */
const CODE_FORMAT = {
  digits: 6,
  example: "123456",
  recoveryExample: "ABCDE-FGHIJ",
  seconds: 30,
} as const;

interface VerifyResponse {
  accessToken: string;
  tenants: Array<{ code: string; name: string }>;
  claims: AtlasClaims;
}

export function MfaChallengePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const mfaToken = useAuthStore((s) => s.mfaToken);
  const setSession = useAuthStore((s) => s.setSession);
  const setActiveTenantCode = useAuthStore((s) => s.setActiveTenantCode);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // setSession clears the challenge token, which would otherwise trip the
  // "no challenge, back to sign-in" redirect below on the way out.
  const completed = useRef(false);

  // Reaching this page without a challenge token means the sign-in was never
  // started, or the page was reloaded (the token is deliberately not
  // persisted). Sending them back beats showing a form that cannot succeed.
  useEffect(() => {
    if (!mfaToken && !completed.current) navigate("/login", { replace: true });
  }, [mfaToken, navigate]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!mfaToken) return;
    setLoading(true);
    setError(null);
    try {
      // Holds the refresh lock, as sign-in does (D1042, lib/api/sign-in.ts).
      const res = await whileSigningIn(() =>
        post<VerifyResponse>("/auth/mfa/verify", {
          mfaToken,
          code: code.trim(),
        }),
      );
      completed.current = true;
      const first = res.tenants[0];
      if (first) setActiveTenantCode(first.code);
      setSession(res.accessToken, res.claims);
      navigate(first ? "/app/home" : "/create-workspace", { replace: true });
    } catch (err) {
      setError(
        err instanceof Error
          ? errorMessage(err, t, i18n.language)
          : t("auth.mfa.notAccepted"),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div style={{ inlineSize: 400, maxInlineSize: "100%" }}>
        <div style={{ textAlign: "center", marginBlockEnd: 20 }}>
          <img src="/icons/atlas.svg" alt="" width="48" height="48" />
          <h1 style={{ margin: "8px 0 0" }}>{t("auth.mfa.heading")}</h1>
        </div>
        <Card>
          <PageHeader
            title={t("auth.mfa.title")}
            subtitle={t("auth.mfa.subtitle", { digits: CODE_FORMAT.digits })}
          />
          <form onSubmit={submit}>
            <div className="atlas-field">
              <label className="atlas-label" htmlFor="mfa-code">
                {t("auth.mfa.codeLabel")}{" "}
                <span style={{ color: "var(--atlas-danger)" }}>*</span>
              </label>
              <Input
                id="mfa-code"
                name="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
                autoComplete="one-time-code"
                // Not type="number": a leading zero matters and recovery codes
                // are not numeric.
                inputMode="text"
                placeholder={t("auth.mfa.codePlaceholder", {
                  example: CODE_FORMAT.example,
                  recoveryExample: CODE_FORMAT.recoveryExample,
                })}
                dir="ltr"
                aria-describedby="mfa-help"
              />
              <div
                id="mfa-help"
                className="atlas-muted"
                style={{ fontSize: 12, marginBlockStart: 4 }}
              >
                {t("auth.mfa.codeHelp", { seconds: CODE_FORMAT.seconds })}
              </div>
            </div>
            {error && (
              <div
                role="alert"
                style={{
                  color: "var(--atlas-danger)",
                  fontSize: 13,
                  marginBlockEnd: 12,
                }}
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              variant="primary"
              disabled={loading || code.trim().length === 0}
              style={{ inlineSize: "100%" }}
            >
              {loading ? t("auth.mfa.submitting") : t("auth.mfa.submit")}
            </Button>
          </form>
          <p
            className="atlas-muted"
            style={{ fontSize: 13, marginBlockStart: 16 }}
          >
            {t("auth.mfa.lostDevice")}
          </p>
          <div
            style={{
              fontSize: 13,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <Link to="/login" replace>
              {t("auth.mfa.backToSignIn")}
            </Link>
            <LanguageSwitch />
          </div>
        </Card>
      </div>
    </div>
  );
}
